import React, { useState, useEffect } from 'react';
import { Navbar as BsNavbar, Container, Nav, NavDropdown, Badge } from 'react-bootstrap';
import { 
    FaBell, 
    FaUser, 
    FaCog, 
    FaSignOutAlt, 
    FaCheckCircle, 
    FaTimesCircle, 
    FaEnvelopeOpenText, 
    FaBirthdayCake, 
    FaHeart,
    FaVolumeUp,
    FaVolumeMute,
    FaDesktop,
    FaBars
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
// Using Firebase direct service
import { getReminders } from '../../services/firebase/reminderService';
import { useWRMS } from '../../wrms/context/WRMSContext';
import { playCelebrationSound, isSoundEnabled, toggleSound } from '../../utils/soundService';
import {
    sendCelebrationDesktopNotification,
    getNotificationPermission,
    requestNotificationPermission,
    sendDesktopNotification
} from '../../utils/desktopNotificationService';
import './Navbar.css';

const Navbar = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // Human-readable role label mapping
    const getRoleLabel = (role) => {
        const labels = {
            'admin': 'Admin',
            'hr': 'HR',
            'manager': 'Manager',
            'dept_head': 'Dept Head',
            'production_head': 'Production Head',
            'director': 'Director',
            'employee': 'Employee',
        };
        return labels[role?.toLowerCase()] || role;
    };
    const [reminders, setReminders] = useState([]);
    const [soundActive, setSoundActive] = useState(() => isSoundEnabled());
    const [desktopPerm, setDesktopPerm] = useState(() => getNotificationPermission());
    
    // Get request notifications from global context
    const { notifications, unreadCount, markRead, markAllRead } = useWRMS();

    useEffect(() => {
        fetchReminders();
    }, []);

    const fetchReminders = async () => {
        try {
            // Fetch reminders for next 7 days for notification badge
            const response = await getReminders(7, null);
            const list = response.data || [];
            setReminders(list);

            // Play celebration sound & push desktop notification once per session if there's a birthday or work anniversary today
            const todayCelebrations = list.filter(r => r.daysUntil === 0 && (r.type === 'birthday' || r.type === 'work_anniversary'));
            if (todayCelebrations.length > 0) {
                const todayKey = new Date().toISOString().split('T')[0];
                const sessionKey = `celebration_sound_played_${todayKey}`;
                if (!sessionStorage.getItem(sessionKey)) {
                    sessionStorage.setItem(sessionKey, 'true');
                    setTimeout(() => {
                        playCelebrationSound();
                    }, 800);
                    // Push to PC / Windows / Mac OS Notification Center
                    todayCelebrations.forEach((cel) => {
                        sendCelebrationDesktopNotification(cel);
                    });
                }
            }
        } catch (error) {
            console.error('Failed to fetch reminders');
        }
    };

    const handleEnableDesktopNotification = async (e) => {
        if (e) e.stopPropagation();
        const res = await requestNotificationPermission();
        setDesktopPerm(res);
        if (res === 'granted') {
            sendDesktopNotification({
                title: 'Digi Sahyadri HRM',
                body: '✅ PC & Mac Desktop Notifications are now active!',
                url: '/dashboard',
            });
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const totalCount = reminders.length + unreadCount;

    return (
        <BsNavbar className="app-navbar shadow-sm py-2 px-1 px-sm-2 px-md-3">
            <Container fluid className="app-navbar-container d-flex align-items-center justify-content-between flex-nowrap">
                <div className="d-flex align-items-center flex-shrink-1" style={{ minWidth: 0 }}>
                    {/* Mobile Hamburger Menu Toggle */}
                    <button
                        type="button"
                        className="navbar-hamburger-btn btn btn-light d-md-none me-2 p-0 border-0 shadow-sm rounded-circle d-flex align-items-center justify-content-center"
                        onClick={() => window.dispatchEvent(new Event('toggle-mobile-sidebar'))}
                        title="Toggle Navigation"
                    >
                        <FaBars size={17} className="text-primary" />
                    </button>
                    <BsNavbar.Brand href="#" className="navbar-brand-custom fw-bold text-primary mb-0 d-flex align-items-center p-0 m-0">
                        <span className="d-none d-md-inline fs-5">Human Resource Management System</span>
                        <span className="d-none d-sm-inline d-md-none fs-6">Digi Sahyadri HRM</span>
                        <span className="d-inline d-sm-none navbar-mobile-brand">Digi Sahyadri</span>
                    </BsNavbar.Brand>
                </div>

                <Nav className="navbar-actions ms-auto align-items-center flex-row flex-shrink-0">

                    {/* Consolidated Notifications Dropdown */}
                    <NavDropdown
                        title={
                            <div className="notification-bell-btn">
                                <FaBell size={17} />
                                {totalCount > 0 && (
                                    <span className="notification-counter-badge">
                                        {totalCount > 99 ? '99+' : totalCount}
                                    </span>
                                )}
                            </div>
                        }
                        id="notification-dropdown"
                        align="end"
                        className="notification-nav-item no-caret"
                    >
                        <div className="notification-scroll-container">
                            {/* Top Control Header: Sound + Desktop OS Notification status */}
                            <div className="dropdown-header d-flex justify-content-between align-items-center py-2 px-3 border-bottom bg-light" style={{ position: 'sticky', top: 0, zIndex: 12 }}>
                                <span className="fw-bold text-dark" style={{ fontSize: '0.8rem' }}>Notifications</span>
                                <div className="d-flex align-items-center gap-2">
                                    {/* Desktop OS Notification Toggle */}
                                    {desktopPerm !== 'granted' ? (
                                        <button 
                                            className="btn btn-outline-primary px-2 py-0 d-flex align-items-center gap-1"
                                            style={{ fontSize: '0.68rem', borderRadius: '12px', height: '22px' }}
                                            title="Enable Windows & Mac Desktop Notifications"
                                            onClick={handleEnableDesktopNotification}
                                        >
                                            <FaDesktop size={10} />
                                            <span>Desktop Alerts</span>
                                        </button>
                                    ) : (
                                        <span 
                                            className="text-success d-inline-flex align-items-center gap-1"
                                            style={{ fontSize: '0.68rem', fontWeight: 600 }}
                                            title="Windows / Mac Desktop Notifications Active"
                                        >
                                            <FaDesktop size={10} />
                                            <span>Desktop Active</span>
                                        </span>
                                    )}

                                    {/* Sound Toggle */}
                                    <button 
                                        className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-1" 
                                        style={{ fontSize: '0.7rem', color: soundActive ? '#4f46e5' : '#94a3b8' }}
                                        title={soundActive ? 'Notification Sound: ON (click to mute)' : 'Notification Sound: MUTED (click to enable)'}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const next = toggleSound();
                                            setSoundActive(next);
                                        }}
                                    >
                                        {soundActive ? <FaVolumeUp size={12} /> : <FaVolumeMute size={12} />}
                                        <span>{soundActive ? 'Sound' : 'Muted'}</span>
                                    </button>

                                    {unreadCount > 0 && (
                                        <button 
                                            className="btn btn-link p-0 text-decoration-none text-primary" 
                                            style={{ fontSize: '0.7rem', fontWeight: 600 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                markAllRead();
                                            }}
                                        >
                                            Mark read
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* 1. Request Notifications */}
                            {notifications && notifications.length > 0 && (
                                <>
                                    <div className="py-1 px-3 bg-light border-bottom text-muted" style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.5px' }}>
                                        LEAVE & WORK REQUESTS
                                    </div>
                                    {notifications.map((notif) => (
                                        <NavDropdown.Item 
                                            key={notif.id} 
                                            className={`notification-item ${!notif.read ? 'unread' : ''} text-wrap border-bottom py-2 px-3`} 
                                            style={{ background: !notif.read ? '#f0f3ff' : '#ffffff' }}
                                            onClick={async () => {
                                                await markRead(notif.id, notif.recipientType);
                                                if (notif.requestId && notif.requestType) {
                                                    navigate(`/wrms/request/${notif.requestType}/${notif.requestId}`);
                                                }
                                            }}
                                        >
                                            <div className="d-flex align-items-start gap-2">
                                                <span className="flex-shrink-0" style={{ marginTop: '2px' }}>
                                                    {notif.type === 'request_approved' ? (
                                                        <FaCheckCircle className="text-success" style={{ fontSize: '1.1rem' }} />
                                                    ) : notif.type === 'request_rejected' ? (
                                                        <FaTimesCircle className="text-danger" style={{ fontSize: '1.1rem' }} />
                                                    ) : (
                                                        <FaEnvelopeOpenText className="text-primary" style={{ fontSize: '1.1rem' }} />
                                                    )}
                                                </span>
                                                <div className="d-flex flex-column">
                                                    <small className="fw-bold text-dark">{notif.title || 'Request Update'}</small>
                                                    <span className="small text-muted">{notif.message}</span>
                                                    <small className="text-muted mt-1" style={{ fontSize: '0.65rem' }}>
                                                        {notif.createdAt ? new Date(notif.createdAt.toDate ? notif.createdAt.toDate() : notif.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Just now'}
                                                    </small>
                                                </div>
                                            </div>
                                        </NavDropdown.Item>
                                    ))}
                                </>
                            )}

                            {/* 2. Reminders / System Events */}
                            {reminders && reminders.length > 0 && (
                                <>
                                    <div className="dropdown-header py-2 px-3 border-bottom bg-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                                        <span className="fw-bold text-dark">System Reminders</span>
                                    </div>
                                    {reminders.map((reminder, idx) => (
                                        <NavDropdown.Item 
                                            key={idx} 
                                            onClick={() => {
                                                if (reminder.type === 'birthday' || reminder.type === 'work_anniversary') {
                                                    playCelebrationSound();
                                                }
                                            }}
                                            className={`notification-item reminder-${reminder.type} text-wrap border-bottom py-2 px-3`} 
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="d-flex align-items-start gap-2">
                                                <span className="flex-shrink-0" style={{ marginTop: '2px' }}>
                                                    {reminder.type === 'birthday' ? (
                                                        <FaBirthdayCake style={{ color: '#ec4899', fontSize: '1.1rem' }} />
                                                    ) : (
                                                        <FaHeart style={{ color: '#10b981', fontSize: '1.1rem' }} />
                                                    )}
                                                </span>
                                                <div className="d-flex flex-column">
                                                    <small className="fw-bold text-dark" style={{ textTransform: 'capitalize' }}>
                                                        {reminder.type} Reminder
                                                    </small>
                                                    <span className="small text-muted">{reminder.message}</span>
                                                    <small className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                                        {new Date(reminder.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    </small>
                                                </div>
                                            </div>
                                        </NavDropdown.Item>
                                    ))}
                                </>
                            )}

                            {/* 3. Empty state */}
                            {(!notifications || notifications.length === 0) && (!reminders || reminders.length === 0) && (
                                <NavDropdown.Item disabled className="text-center py-4 text-muted">
                                    No new notifications
                                </NavDropdown.Item>
                            )}
                        </div>
                    </NavDropdown>

                    {/* User Dropdown */}
                    {(() => {
                        const userPhoto = user?.photoURL || user?.photo || user?.personal?.photo || user?.personalInfo?.photo || '';
                        const userName = user?.firstName || user?.employeeName || user?.displayName || user?.name || user?.email?.split('@')[0] || 'User';
                        const userRole = user?.role ? getRoleLabel(user.role) : '';
                        return (
                            <NavDropdown
                                title={
                                    <div className="user-profile-pill d-inline-flex align-items-center">
                                        {userPhoto ? (
                                            <img
                                                src={userPhoto}
                                                alt="Profile"
                                                className="user-avatar-img rounded-circle me-1 me-sm-2 shadow-sm"
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                            />
                                        ) : (
                                            <div className="user-avatar-placeholder me-1 me-sm-2 d-flex align-items-center justify-content-center">
                                                <FaUser size={12} />
                                            </div>
                                        )}
                                        <span className="user-name-text">
                                            {userName}
                                        </span>
                                        {userRole && (
                                            <Badge bg="primary" className="user-role-badge ms-1 ms-sm-2 d-none d-sm-inline-block">
                                                {userRole}
                                            </Badge>
                                        )}
                                    </div>
                                }
                                id="user-dropdown"
                                align="end"
                                className="user-nav-item"
                            >
                                <div className="user-dropdown-header px-3 py-2 border-bottom">
                                    <div className="d-flex align-items-center gap-2">
                                        {userPhoto ? (
                                            <img
                                                src={userPhoto}
                                                alt="Profile"
                                                className="rounded-circle shadow-sm"
                                                style={{ width: '36px', height: '36px', objectFit: 'cover', border: '2px solid #2563eb' }}
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                            />
                                        ) : (
                                            <div 
                                                className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center shadow-sm flex-shrink-0"
                                                style={{ width: '36px', height: '36px', fontSize: '0.9rem', fontWeight: 'bold' }}
                                            >
                                                {(userName?.[0] || 'U').toUpperCase()}
                                            </div>
                                        )}
                                        <div className="d-flex flex-column text-truncate" style={{ minWidth: 0, flex: 1 }}>
                                            <span className="fw-bold text-dark text-truncate" style={{ fontSize: '0.85rem' }}>
                                                {userName}
                                            </span>
                                            {user?.email && (
                                                <span className="text-muted text-truncate" style={{ fontSize: '0.72rem' }}>
                                                    {user.email}
                                                </span>
                                            )}
                                            {userRole && (
                                                <div className="mt-1">
                                                    <Badge bg="primary" style={{ fontSize: '0.62rem' }}>
                                                        {userRole}
                                                    </Badge>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <NavDropdown.Item onClick={() => navigate('/my-profile')}>
                                    <FaUser className="me-2 text-primary" />
                                    Profile
                                </NavDropdown.Item>
                                <NavDropdown.Item onClick={() => navigate('/settings')}>
                                    <FaCog className="me-2 text-muted" />
                                    Settings
                                </NavDropdown.Item>
                                <NavDropdown.Divider className="my-1" />
                                <NavDropdown.Item onClick={handleLogout} className="text-danger">
                                    <FaSignOutAlt className="me-2" />
                                    Logout
                                </NavDropdown.Item>
                            </NavDropdown>
                        );
                    })()}
                </Nav>
            </Container>
        </BsNavbar>
    );
};

export default Navbar;
